/**
 * Stage 2.2 — ONE `getMyClientWork` read per client shell.
 *
 * The shell's badges, the dashboard's "Ootab sind" / "RM24 tegeleb" and the
 * "Minu objektid" block all read this, so a badge and the list it counts can
 * never come from two different fetches. Re-read when the window regains
 * focus (throttled), on `refresh()`, and after every successful callable
 * listed in `services/clientWorkSignal.ts` (approve/reject hours, change
 * orders, replies, order changes) — no screen has to remember to refresh.
 *
 * `messagesUnread` is the shell's LIVE company-inbox count; it overrides the
 * server snapshot so the Sõnumid badge moves the moment a thread is read.
 */
import type { ClientAttention } from '@rendimees24/shared-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { getMyClientWork, type MyClientWork } from '../services/clientWork';
import { onClientWorkChanged } from '../services/clientWorkSignal';

import { useActiveCompany } from './ActiveCompanyContext';

/** A focus within this window of the last read does not re-read. */
const FOCUS_THROTTLE_MS = 30_000;
/**
 * Actions announced within this window collapse into one re-read (approving
 * ten sheets in a row is one read, not ten).
 */
export const CHANGE_DEBOUNCE_MS = 300;

export type ClientWorkStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ClientWorkValue {
  /** `idle`: no company to read for. */
  status: ClientWorkStatus;
  clientId: string | null;
  work: MyClientWork | null;
  attention: ClientAttention;
  error: unknown;
  refresh: () => Promise<void>;
}

const EMPTY: ClientAttention = {
  mine: [],
  rm24: [],
  counts: { orders: 0, hours: 0, invoices: 0, messages: 0 },
  asOf: '',
};

const ClientWorkContext = createContext<ClientWorkValue | null>(null);

export function ClientWorkProvider({
  children,
  messagesUnread,
}: {
  children: ReactNode;
  messagesUnread?: number;
}) {
  const { clientId: activeId, loading: companyLoading } = useActiveCompany();
  const clientId = activeId ?? null;
  const [work, setWork] = useState<MyClientWork | null>(null);
  const [status, setStatus] = useState<ClientWorkStatus>(clientId ? 'loading' : 'idle');
  const [error, setError] = useState<unknown>(null);
  const lastRead = useRef(0);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++seq.current;
    if (!clientId) {
      setWork(null);
      setStatus(companyLoading ? 'loading' : 'idle');
      return;
    }
    lastRead.current = Date.now();
    setStatus((s) => (s === 'ready' ? s : 'loading'));
    try {
      const data = await getMyClientWork(clientId);
      if (mySeq !== seq.current) return;
      setWork(data);
      setError(null);
      setStatus('ready');
    } catch (e) {
      if (mySeq !== seq.current) return;
      setError(e);
      setStatus('error');
    }
  }, [clientId, companyLoading]);

  useEffect(() => {
    setWork(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!clientId) return;
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastRead.current < FOCUS_THROTTLE_MS) return;
      void load();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [clientId, load]);

  // An action that changes a counted item (approve/reject hours, decide a
  // change order, reply to RM24, order changes) was announced by the callable
  // wrapper — re-read so the badge, „Ootab sind” and the page agree.
  useEffect(() => {
    if (!clientId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = onClientWorkChanged(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        void load();
      }, CHANGE_DEBOUNCE_MS);
    });
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [clientId, load]);

  const value = useMemo<ClientWorkValue>(() => {
    const base = status === 'ready' && work?.attention ? work.attention : EMPTY;
    const attention =
      typeof messagesUnread === 'number'
        ? { ...base, counts: { ...base.counts, messages: messagesUnread } }
        : base;
    return { status, clientId, work, attention, error, refresh: load };
  }, [status, clientId, work, error, load, messagesUnread]);

  return <ClientWorkContext.Provider value={value}>{children}</ClientWorkContext.Provider>;
}

/** The shell's client work. Throws outside a `ClientWorkProvider`. */
export function useClientWork(): ClientWorkValue {
  const value = useContext(ClientWorkContext);
  if (!value) throw new Error('useClientWork outside ClientWorkProvider');
  return value;
}

/**
 * Renders `children` inside the shell's provider, or a provider of its own
 * when there is none (a page rendered on its own, e.g. in a test).
 */
export function EnsureClientWork({ children }: { children: ReactNode }) {
  const existing = useContext(ClientWorkContext);
  return existing ? <>{children}</> : <ClientWorkProvider>{children}</ClientWorkProvider>;
}
